import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
    { to: "/", label: "Home", end: true },
    { to: "/services", label: "Services" },
    { to: "/projects", label: "Projects" },
    { to: "/process", label: "Process" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[#0D0D0D]/85 backdrop-blur-xl border-b border-[#262626]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="container-pad mx-auto flex items-center justify-between h-20">
                <Link to="/" data-testid="logo-link" className="flex items-baseline gap-2 group">
                    <span className="font-display text-base md:text-lg text-gold tracking-tight italic font-semibold">
                        Midasis
                    </span>
                    <span className="font-display text-xl md:text-2xl text-cream tracking-tight font-light hidden sm:inline">
                        Elite Interiors
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `nav-link text-sm tracking-wide ${isActive ? "active" : ""}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:block">
                    <Link to="/contact" data-testid="navbar-cta-button" className="btn-primary">
                        Book Free Consultation
                    </Link>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    aria-label="Open menu"
                    className="lg:hidden text-cream"
                    onClick={() => setOpen((v) => !v)}
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-[#0D0D0D] border-t border-[#262626]"
                >
                    <div className="container-pad py-8 flex flex-col gap-6">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `font-display text-3xl ${isActive ? "text-gold" : "text-cream"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link to="/contact" className="btn-primary mt-4 w-fit" data-testid="mobile-cta-button">
                            Book Free Consultation
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
